// src/services/cnpj-external/cnpjUniversalPojo.js
// *******************************************************************************
// CNPJ UNIVERSAL POJO - Entity Schema Aligned Structure
// *******************************************************************************
// This file defines the universal POJO structure for CNPJ API responses.
// The structure aligns with the backend entity schema (entitiesSchema.js)
// to enable seamless form population and data persistence.
//
// Backend schema references:
// - entitiesSchema.js - Main entity structure
// - entityCompanyFields.js - Company-specific fields
// - documentSchema.js - Documents array
// - contactSchema.js - Contacts array
// - addressSchema.js - Addresses array
// - activitySchema.js - Activities (CNAE)
// - holderSchema.js - Holders/partners
// - fiscalDataSchema.js - Fiscal data
// - registrationStatusSchema.js - Registration status
// *******************************************************************************

import {
  fixOldBrazilianMobile,
  validateBrazilianMobile,
  validateBrazilianLandline,
  validateBrazilianFree,
  validateBrazilianRobot,
} from 'utils/phone.js';

/**
 * Universal POJO structure for CNPJ API responses
 * Matches the entity schema from backend for seamless integration
 *
 * @typedef {Object} CnpjUniversalPojo
 *
 * @property {string} provider - Provider name (e.g., 'brasilapi', 'cnpjws')
 * @property {boolean} success - Whether the lookup was successful
 * @property {boolean} apiWorked - Whether the API responded (true even if CNPJ not found)
 * @property {string|null} error - Error message if any
 * @property {string} source - Data source identifier
 *
 * --- Entity Base Fields ---
 * @property {string} entityType - 'company'
 * @property {string[]} entityProfile - ['company'] or other profiles
 * @property {string} countryCode - 'BR' for Brazilian companies
 *
 * --- Company Names (entityNamesFields.js) ---
 * @property {string|null} name - Trade name (nome fantasia)
 * @property {string|null} fullName - Legal name (razao social)
 *
 * --- Dates ---
 * @property {string|null} foundedAt - Foundation date (ISO format)
 *
 * --- Documents Array (documentSchema.js) ---
 * @property {Object[]} documents - Array of documents
 * @property {string} documents[].documentType - 'cnpj', 'cpf', 'rg', etc.
 * @property {string} documents[].value - Document value (numbers only)
 * @property {string} documents[].countryCode - 'BR'
 * @property {boolean} documents[].isPrimary - Is primary document
 *
 * --- Contacts Array (contactSchema.js) ---
 * @property {Object[]} contacts - Array of contacts
 * @property {string} contacts[].contactType - 'phone', 'mobile', 'email', 'fax'
 * @property {string} contacts[].phoneType - 'landline', 'mobile', 'fax' (for phone types)
 * @property {string} contacts[].value - Contact value
 * @property {string} contacts[].countryCode - Country code for phones
 * @property {boolean} contacts[].isPrimary - Is primary contact
 *
 * --- Addresses Array (addressSchema.js) ---
 * @property {Object[]} addresses - Array of addresses
 * @property {string} addresses[].addressType - 'main', 'billing', 'shipping', etc.
 * @property {string} addresses[].zip - ZIP code (CEP)
 * @property {string} addresses[].address1 - Street name
 * @property {string} addresses[].address2 - Complement
 * @property {string} addresses[].number - Street number
 * @property {string} addresses[].neighborhood - Neighborhood (bairro)
 * @property {string} addresses[].city - City name
 * @property {string} addresses[].cityCode - IBGE city code
 * @property {string} addresses[].region - State (UF)
 * @property {string} addresses[].countryCode - 'BR'
 * @property {boolean} addresses[].isPrimary - Is primary address
 *
 * --- Activities Array (activitySchema.js) ---
 * @property {Object[]} activities - Array of CNAE activities
 * @property {string} activities[].code - CNAE code
 * @property {string} activities[].description - Activity description
 * @property {boolean} activities[].isPrimary - Is main activity
 *
 * --- Holders Array (holderSchema.js) ---
 * @property {Object[]} holders - Array of partners/shareholders
 * @property {string} holders[].name - Partner name
 * @property {string} holders[].cpf - Partner CPF (numbers only)
 * @property {string} holders[].qualification - Partner qualification/role
 * @property {string} holders[].qualificationCode - Qualification code
 * @property {string} holders[].sinceAt - Start date (ISO format)
 * @property {Object} holders[].legalRepresentative - Legal representative data
 *
 * --- Fiscal Data (fiscalDataSchema.js) ---
 * @property {Object} fiscalData - Fiscal information
 * @property {string} fiscalData.mainCnae - Main CNAE code
 * @property {string} fiscalData.taxRegime - Tax regime (simplesNacional, lucroReal, etc.)
 * @property {boolean} fiscalData.isMei - Is MEI (Microempreendedor Individual)
 * @property {string|null} fiscalData.simplesOptedAt - Simples Nacional opt-in date
 * @property {string|null} fiscalData.simplesExcludedAt - Simples Nacional exclusion date
 *
 * --- Registration Status (registrationStatusSchema.js) ---
 * @property {Object} registrationStatus - Registration status with Receita Federal
 * @property {string} registrationStatus.status - Status code (2=active, 3=suspended, etc.)
 * @property {string} registrationStatus.statusText - Status text (Ativa, Baixada, etc.)
 * @property {string|null} registrationStatus.date - Status date
 * @property {string|null} registrationStatus.reason - Reason for status
 * @property {string|null} registrationStatus.specialStatus - Special status if any
 * @property {string|null} registrationStatus.specialStatusDate - Special status date
 *
 * --- State Registrations (Inscrições Estaduais) ---
 * @property {Object[]} stateRegistrations - Array of state tax registrations
 * @property {string} stateRegistrations[].stateCode - State code (UF, e.g., 'SP', 'RJ')
 * @property {string} stateRegistrations[].value - State registration number (Inscrição Estadual)
 * @property {boolean} stateRegistrations[].isActive - Whether the registration is active
 * @property {string|null} stateRegistrations[].updatedAt - Last update date (ISO format)
 *
 * --- Other Company Fields (entityCompanyFields.js) ---
 * @property {string|null} legalNature - Legal nature (e.g., '206-2 - Sociedade Empresária Limitada')
 * @property {string|null} legalNatureCode - Legal nature code
 * @property {number|null} shareCapital - Share capital (capital social)
 * @property {string|null} size - Company size (ME, EPP, DEMAIS)
 * @property {string|null} segment - Business segment
 */

/**
 * Creates an empty CNPJ Universal POJO with default values
 * Use this as a template when creating adapters
 *
 * @returns {CnpjUniversalPojo} Empty POJO with default structure
 */
export function createEmptyCnpjPojo() {
  return {
    // Provider metadata
    provider: null,
    success: false,
    apiWorked: false,
    error: null,
    source: null,

    // Entity base
    entityType: 'company',
    entityProfile: ['company'],
    countryCode: 'BR',

    // Company names
    name: null,
    fullName: null,

    // Dates
    foundedAt: null,

    // Documents (will contain CNPJ)
    documents: [],

    // Contacts (phone, email)
    contacts: [],

    // Addresses
    addresses: [],

    // Activities (CNAE)
    activities: [],

    // Holders (partners)
    holders: [],

    // State Registrations (Inscrições Estaduais)
    stateRegistrations: [],

    // Fiscal data
    fiscalData: {
      mainCnae: null,
      taxRegime: null,
      isMei: false,
      simplesOptedAt: null,
      simplesExcludedAt: null,
    },

    // Registration status
    registrationStatus: {
      status: null,
      statusText: null,
      date: null,
      reason: null,
      specialStatus: null,
      specialStatusDate: null,
    },

    // Other company fields
    legalNature: null,
    legalNatureCode: null,
    shareCapital: null,
    size: null,
    segment: null,
  };
}

/**
 * Creates a failure POJO for when API fails or CNPJ not found
 *
 * @param {Object} options - Options
 * @param {string} options.provider - Provider name
 * @param {boolean} options.apiWorked - Whether API responded
 * @param {string} options.error - Error message
 * @returns {CnpjUniversalPojo} Failure POJO
 */
export function createFailureCnpjPojo({ provider, apiWorked = false, error }) {
  return {
    ...createEmptyCnpjPojo(),
    provider,
    success: false,
    apiWorked,
    error,
    source: provider,
  };
}

/**
 * Maps registration status code to entity status
 * Used to align with backend registrationStatusSchema
 *
 * @param {string} statusText - Status text from API (e.g., 'ATIVA', 'BAIXADA')
 * @returns {number|null} Status code (2=active, 3=suspended, etc.)
 */
export function mapRegistrationStatus(statusText) {
  if (!statusText) return null;

  const upperStatus = String(statusText).toUpperCase().trim();

  const statusMap = {
    ATIVA: 2,
    SUSPENSA: 3,
    INAPTA: 4,
    BAIXADA: 5,
    NULA: 6,
  };

  return statusMap[upperStatus] || null;
}

/**
 * Parses Brazilian date formats to ISO string
 * Handles formats: DD/MM/YYYY, YYYY-MM-DD
 *
 * @param {string} dateStr - Date string
 * @returns {string|null} ISO date string or null
 */
export function parseBrazilianDate(dateStr) {
  if (!dateStr) return null;

  // Already in ISO format (YYYY-MM-DD)
  if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
    return dateStr;
  }

  // Brazilian format (DD/MM/YYYY)
  const match = dateStr.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (match) {
    const [, day, month, year] = match;
    return `${year}-${month}-${day}`;
  }

  return null;
}

/**
 * Cleans phone number to only digits and fixes old Brazilian mobile numbers
 * Old mobile numbers (before 2012-2016) may be missing the leading "9" digit
 *
 * @param {string} phone - Phone number with any formatting
 * @returns {string} Digits only, with old mobile numbers corrected
 *
 * @example
 * cleanPhoneNumber('(11) 8232-0000') // '11982320000' (adds missing 9)
 * cleanPhoneNumber('(11) 3232-0000') // '1132320000' (valid landline, no change)
 * cleanPhoneNumber('(11) 98232-0000') // '11982320000' (already valid mobile)
 */
export function cleanPhoneNumber(phone) {
  if (!phone) return '';
  const digits = String(phone).replace(/\D/g, '');
  // Fix old Brazilian mobile numbers missing the "9" prefix
  return fixOldBrazilianMobile(digits);
}

/**
 * Derives the correct phoneType from cleaned phone digits
 * Uses validation functions to determine if the number is mobile, landline, free, or robot
 *
 * @param {string} digits - Cleaned phone digits (after cleanPhoneNumber)
 * @returns {string} Phone type: 'mobile', 'landline', 'free', 'robot', or 'landline' as default
 *
 * @example
 * derivePhoneType('11982320000') // 'mobile' (11 digits, starts with 9 after DDD)
 * derivePhoneType('1132320000')  // 'landline' (10 digits, starts with 3 after DDD)
 * derivePhoneType('08006430000') // 'free' (0800 number)
 * derivePhoneType('30034000')    // 'robot' (3003/4004 number)
 */
export function derivePhoneType(digits) {
  if (!digits) return 'landline';
  const d = String(digits).replace(/\D/g, '');
  if (validateBrazilianMobile(d)) return 'mobile';
  if (validateBrazilianFree(d)) return 'free';
  if (validateBrazilianRobot(d)) return 'robot';
  if (validateBrazilianLandline(d)) return 'landline';
  return 'landline';
}

/**
 * Cleans CNPJ to only digits
 *
 * @param {string} cnpj - CNPJ with any formatting
 * @returns {string} Digits only (14 digits)
 */
export function cleanCnpj(cnpj) {
  if (!cnpj) return '';
  return String(cnpj).replace(/\D/g, '').padStart(14, '0');
}

/**
 * Cleans CEP to only digits
 *
 * @param {string} cep - CEP with any formatting
 * @returns {string} Digits only (8 digits)
 */
export function cleanCep(cep) {
  if (!cep) return '';
  return String(cep).replace(/\D/g, '').padStart(8, '0');
}

// *******************************************************************************
// CNAE TO ISIC SECTION MAPPING
// *******************************************************************************
// ISIC Rev.4 - International Standard Industrial Classification (UN)
// Maps CNAE division (first 2 digits) to ISIC section code (A-U)
// Based on: https://unstats.un.org/unsd/classifications/Family/Detail/27

/**
 * Maps CNAE division (first 2 digits) to ISIC section code
 * CNAE Brazilian classification aligns with ISIC Rev.4
 */
const CNAE_DIVISION_TO_ISIC_SECTION = {
  // A - Agriculture, forestry and fishing
  '01': 'A', '02': 'A', '03': 'A',
  // B - Mining and quarrying
  '05': 'B', '06': 'B', '07': 'B', '08': 'B', '09': 'B',
  // C - Manufacturing
  '10': 'C', '11': 'C', '12': 'C', '13': 'C', '14': 'C', '15': 'C',
  '16': 'C', '17': 'C', '18': 'C', '19': 'C', '20': 'C', '21': 'C',
  '22': 'C', '23': 'C', '24': 'C', '25': 'C', '26': 'C', '27': 'C',
  '28': 'C', '29': 'C', '30': 'C', '31': 'C', '32': 'C', '33': 'C',
  // D - Electricity, gas, steam and air conditioning supply
  '35': 'D',
  // E - Water supply; sewerage, waste management
  '36': 'E', '37': 'E', '38': 'E', '39': 'E',
  // F - Construction
  '41': 'F', '42': 'F', '43': 'F',
  // G - Wholesale and retail trade; repair of motor vehicles
  '45': 'G', '46': 'G', '47': 'G',
  // H - Transportation and storage
  '49': 'H', '50': 'H', '51': 'H', '52': 'H', '53': 'H',
  // I - Accommodation and food service activities
  '55': 'I', '56': 'I',
  // J - Information and communication
  '58': 'J', '59': 'J', '60': 'J', '61': 'J', '62': 'J', '63': 'J',
  // K - Financial and insurance activities
  '64': 'K', '65': 'K', '66': 'K',
  // L - Real estate activities
  '68': 'L',
  // M - Professional, scientific and technical activities
  '69': 'M', '70': 'M', '71': 'M', '72': 'M', '73': 'M', '74': 'M', '75': 'M',
  // N - Administrative and support service activities
  '77': 'N', '78': 'N', '79': 'N', '80': 'N', '81': 'N', '82': 'N',
  // O - Public administration and defence
  '84': 'O',
  // P - Education
  '85': 'P',
  // Q - Human health and social work activities
  '86': 'Q', '87': 'Q', '88': 'Q',
  // R - Arts, entertainment and recreation
  '90': 'R', '91': 'R', '92': 'R', '93': 'R',
  // S - Other service activities
  '94': 'S', '95': 'S', '96': 'S',
  // T - Activities of households as employers
  '97': 'T', '98': 'T',
  // U - Activities of extraterritorial organizations
  '99': 'U',
};

/**
 * Derives ISIC section code from CNAE code
 * Can receive either full CNAE code or just the section letter
 *
 * @param {string|number} cnaeOrSection - CNAE code (e.g., '9602502', 9602502) or section letter (e.g., 'S')
 * @returns {string|null} ISIC section code (A-U) or null if not found
 *
 * @example
 * getIsicSectionFromCnae('9602502') // returns 'S'
 * getIsicSectionFromCnae(4772500)   // returns 'G'
 * getIsicSectionFromCnae('S')       // returns 'S' (already a section)
 */
export function getIsicSectionFromCnae(cnaeOrSection) {
  if (!cnaeOrSection) return null;

  const value = String(cnaeOrSection).trim().toUpperCase();

  // If it's already a single letter section code (A-U), return it
  if (/^[A-U]$/.test(value)) {
    return value;
  }

  // Extract first 2 digits (division) from CNAE
  const cnaeDigits = value.replace(/\D/g, '');
  if (cnaeDigits.length < 2) return null;

  const division = cnaeDigits.substring(0, 2);
  return CNAE_DIVISION_TO_ISIC_SECTION[division] || null;
}

// *******************************************************************************
// EXPORTS
// *******************************************************************************
export default {
  createEmptyCnpjPojo,
  createFailureCnpjPojo,
  mapRegistrationStatus,
  parseBrazilianDate,
  cleanPhoneNumber,
  cleanCnpj,
  cleanCep,
  getIsicSectionFromCnae,
  CNAE_DIVISION_TO_ISIC_SECTION,
};
